import { AbstractPasswordEncoder } from './password.ts';
import { argon2, keygen } from '@interopio/gateway-server/tools';

/**
 * Constant-time buffer comparison to prevent timing attacks.
 *
 * @param a - First buffer
 * @param b - Second buffer
 * @returns true if buffers are equal, false otherwise
 */
function bufferEquals(a: Buffer, b: Buffer): boolean {
    if (a.length !== b.length) {
        return false;
    }
    let diff = 0;
    for (let i = 0; i < a.length; i++) {
        diff |= a[i] ^ b[i];
    }
    return diff === 0;
}



export class Argon2PasswordEncoder extends AbstractPasswordEncoder {
    readonly #saltLength: number;
    readonly #hashLength: number;
    readonly #parallelism: number;
    readonly #memory: number
    readonly #passes: number;

    constructor(
        saltLength: number = argon2.DEFAULT_SALT_LENGTH,
        hashLength: number = argon2.DEFAULT_HASH_LENGTH,
        parallelism: number = argon2.DEFAULT_PARALLELISM,
        memory: number = argon2.DEFAULT_MEMORY,
        passes: number = argon2.DEFAULT_PASSES
    ) {
        super();
        this.#saltLength = saltLength;
        this.#hashLength = hashLength;
        this.#parallelism = parallelism;
        this.#memory = memory;
        this.#passes = passes;
    }

    async matchesDefined(rawPassword: string, encodedPassword: string): Promise<boolean> {
        try {
            const decoded = argon2.decode(encodedPassword);
            const hash = await argon2.createHash(
                decoded.algorithm,
                rawPassword,
                decoded.hash.length,
                decoded.parameters
            );
            return bufferEquals(decoded.hash, hash);
        } catch {
            return false;
        }
    }

    async encodeDefinedPassword(rawPassword: string): Promise<string> {
        const nonce = keygen.createSalt(this.#saltLength);
        const parameters = {
            memory: this.#memory,
            passes: this.#passes,
            parallelism: this.#parallelism,
            nonce
        };
        const hash = await argon2.createHash(
            'argon2id',
            rawPassword,
            this.#hashLength,
            parameters
        );
        return argon2.encode({
            algorithm: 'argon2id',
            version: argon2.ARGON2_VERSION,
            parameters,
            hash
        });
    }

    upgradeEncodingDefined(encodedPassword: string): boolean {
        const decoded = argon2.decode(encodedPassword);
        return decoded.version < argon2.ARGON2_VERSION || decoded.parameters.memory < this.#memory || decoded.parameters.passes < this.#passes;
    }
}
